// Choosing a Tally company BY HAND — walk the folders, pick the one that is the
// client, name it, connect it.
//
// ⚠️ THIS EXISTS BECAUSE THE SCAN CANNOT READ A FILING PLAN. It finds every
// company on the disk, but which of them the firm treats as a client — and
// which is last month's backup, a year folder, or a subsidiary the partner
// counts separately — is a judgement only the firm holds. Measured on one real
// practice: 472 company folders, 207 of them copies, filed between one and seven
// levels deep with no consistent shape. No inference gets that right every time,
// and the ones it gets wrong are invisible.
//
// ⚠️ THE NAME IS READ FROM TALLY'S OWN FILE, NOT FROM THE FOLDER. `Company.NNN`
// carries the company's real name — `AKPR\AKPR Partnership` holds
// "A K P R AND ASSOCIATES" — and that is what a connection must be bound to. A
// folder name put in `SVCURRENTCOMPANY` gets an EMPTY ENVELOPE from Tally rather
// than an error, so the connection reports itself healthy and syncs nothing.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Select, EmptyState, Skeleton, useToast } from '../../../design-system';

// The agent reaches for work every 30s, so a listing is not instant. Polled
// rather than pushed for the reason the whole module is: the server cannot
// call a PC behind office NAT.
const POLL_MS = 1500;
const POLL_TIMEOUT_MS = 90_000;

function TallyBrowser({ onConnected }) {
  const toast = useToast();

  // Fetched here rather than passed in: this tab is reachable on its own and
  // a parent that happened not to have loaded its machines yet would render
  // an empty picker that looks like "no machines".
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  const [booting, setBooting] = useState(true);

  const [agentId, setAgentId] = useState('');
  const [path, setPath] = useState('');
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  // The company the user has picked, and what they want it called.
  const [picked, setPicked] = useState(null);
  const [customName, setCustomName] = useState('');
  const [clientId, setClientId] = useState('');
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async (targetPath) => {
    if (!agentId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.post('/api/tally/browse', { agentInstallId: agentId, path: targetPath || '' });
      const started = Date.now();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, POLL_MS));
        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/api/tally/browse/${data.jobId}`);
        if (res.data.status === 'succeeded') {
          setListing(res.data);
          setPath(res.data.path || '');
          return;
        }
        if (res.data.status === 'failed') {
          setError(res.data.error || 'That folder could not be read.');
          return;
        }
        if (Date.now() - started > POLL_TIMEOUT_MS) {
          // ⚠️ SAYS WHICH OF THE TWO IT IS. "Timed out" alone reads as a bug in
          // this screen; the honest answer is that the machine has not picked
          // the job up, which is a different thing to go and check.
          setError('That machine has not answered. Is the agent running on it?');
          return;
        }
      }
    } catch (err) {
      const status = err.response?.status;
      setError(
        `${err.response?.data?.error || 'Could not ask that machine to list the folder'}`
        + `${status ? ` (HTTP ${status})` : ''}`,
      );
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    let live = true;
    Promise.all([
      apiClient.get('/api/credentials/agents/mine').catch(() => ({ data: [] })),
      apiClient.get('/api/clients/options').catch(() => ({ data: [] })),
    ]).then(([m, c]) => {
      if (!live) return;
      const list = Array.isArray(m.data) ? m.data : (m.data?.agents || []);
      setMachines(list);
      setClients(Array.isArray(c.data) ? c.data : (c.data?.clients || []));
      if (list[0]) setAgentId(String(list[0].id || list[0]._id));
      setBooting(false);
    });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (agentId) load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const entries = useMemo(() => {
    const all = listing?.entries || [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    // Matches the folder name OR the company's real name — somebody searching
    // for "AKPR" and somebody searching for "A K P R AND ASSOCIATES" are both
    // looking for the same row.
    return all.filter((e) => `${e.name} ${e.companyName}`.toLowerCase().includes(q));
  }, [listing, filter]);

  const choose = (entry) => {
    setPicked(entry);
    setCustomName(entry.companyName || entry.name);
  };

  const connect = async () => {
    // A client, or a name to make one — one of the two.
    if (!picked || (!clientId && !customName.trim())) return;
    setConnecting(true);
    try {
      await apiClient.post('/api/tally/companies/connect', {
        clientId,
        agentInstallId: agentId,
        // ⚠️ THE NAME TALLY GAVE, EVEN WHEN THE USER TYPED A LABEL. A custom
        // name is for the client record; the connection has to carry what Tally
        // will answer to, or it binds to a company that does not exist.
        companyName: picked.companyName || picked.name,
        companyGuid: picked.guid || '',
        // ⚠️⚠️ THE TWO FIELDS THAT MAKE SYNC POSSIBLE. The agent opens a company
        // by writing `Data=<dataPath>` and `Load=<number>` into a scratch
        // tally.ini — without both, the connection is never opened and syncs
        // nothing while looking healthy.
        dataPath: listing?.path || '',
        companyNumber: picked.number || '',
        // ⚠️ EITHER AN EXISTING CLIENT OR A NAME TO CREATE ONE FROM, never
        // both. Picking a company by hand usually IS the moment the client
        // record gets made — the name has just been read off Tally's own file,
        // and sending somebody to a different screen to retype it is exactly
        // the retyping this change removes.
        ...(clientId ? { clientId } : { newClientName: customName.trim() }),
      });
      toast.success(`Connected ${customName || picked.companyName || picked.name}`);
      setPicked(null);
      setCustomName('');
      setClientId('');
      onConnected?.();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not connect that company');
    } finally {
      setConnecting(false);
    }
  };

  // Breadcrumb from the root down, so "where am I" needs no reading of a path.
  const crumbs = useMemo(() => {
    const root = listing?.root || '';
    const here = listing?.path || '';
    if (!root || !here) return [];
    const sep = here.includes('\\') ? '\\' : '/';
    const rootParts = root.split(/[\\/]+/).filter(Boolean);
    const parts = here.split(/[\\/]+/).filter(Boolean);
    const out = [{ label: root, path: root }];
    for (let i = rootParts.length; i < parts.length; i += 1) {
      out.push({ label: parts[i], path: parts.slice(0, i + 1).join(sep) });
    }
    return out;
  }, [listing]);

  if (booting) return <Skeleton height={180} />;

  if (!machines.length) {
    return (
      <EmptyState
        icon="🖥️"
        title="No machine is connected yet"
        description="Install the agent on the PC that holds your Tally data, then come back here."
      />
    );
  }

  return (
    <div className="tb-wrap">
      <div className="tb-toolbar">
        <Select value={agentId} onChange={(e) => { setAgentId(e.target.value); setListing(null); setPicked(null); }}>
          {machines.map((m) => {
            const id = String(m.id || m._id);
            return <option key={id} value={id}>{m.machineName || m.label || id}</option>;
          })}
        </Select>
        <Input
          placeholder="Search this folder…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="tb-search"
        />
        <Button variant="secondary" onClick={() => load(path)} disabled={loading}>
          {loading ? 'Reading…' : '↻ Refresh'}
        </Button>
      </div>

      {crumbs.length > 0 && (
        <nav className="tb-crumbs" aria-label="Folder path">
          {crumbs.map((c, i) => (
            <span key={c.path}>
              {i > 0 && <span className="tb-crumb-sep">›</span>}
              <button
                type="button"
                className={`tb-crumb${i === crumbs.length - 1 ? ' tb-crumb-here' : ''}`}
                onClick={() => load(c.path)}
                disabled={loading || i === crumbs.length - 1}
              >
                {c.label}
              </button>
            </span>
          ))}
        </nav>
      )}

      {error && <p className="tb-error">{error}</p>}

      {loading && !listing && <Skeleton height={220} />}

      {listing && !loading && (
        <div className="tb-list">
          {listing.parent && (
            <button type="button" className="tb-row tb-row-up" onClick={() => load(listing.parent)}>
              <span className="tb-icon">↰</span>
              <span className="tb-name">Up one folder</span>
            </button>
          )}

          {entries.length === 0 && (
            <p className="tb-empty">
              {filter ? 'Nothing here matches that search.' : 'This folder has no sub-folders.'}
            </p>
          )}

          {entries.map((e) => (
            <div key={e.path} className={`tb-row${e.isCompany ? ' tb-row-company' : ''}${picked?.path === e.path ? ' tb-row-picked' : ''}`}>
              <span className="tb-icon">{e.isCompany ? '📒' : e.hasCompaniesInside ? '📂' : '📁'}</span>
              <span className="tb-name">
                {/* The company's REAL name leads; the folder it lives in is the
                    smaller line, because the folder is how you find it and the
                    name is what it is. */}
                {e.isCompany && e.companyName ? (
                  <>
                    <strong>{e.companyName}</strong>
                    <span className="tb-sub">{e.name}{e.number ? ` · ${e.number}` : ''}</span>
                  </>
                ) : (
                  <>
                    <strong>{e.name}</strong>
                    {e.hasCompaniesInside && <span className="tb-sub">contains companies</span>}
                    {!e.hasCompaniesInside && e.folderCount > 0 && (
                      <span className="tb-sub">{e.folderCount} folder{e.folderCount === 1 ? '' : 's'}</span>
                    )}
                  </>
                )}
              </span>
              {e.isCompany ? (
                <Button size="sm" variant={picked?.path === e.path ? 'primary' : 'secondary'} onClick={() => choose(e)}>
                  {picked?.path === e.path ? 'Selected' : 'Select'}
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => load(e.path)} disabled={loading}>
                  Open
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {picked && (
        <div className="tb-confirm">
          <h4>Connect this company</h4>
          <p className="tb-confirm-path">{picked.path}</p>

          <label className="tb-field">
            <span>Name in this software</span>
            <Input value={customName} onChange={(e) => setCustomName(e.target.value)} />
            {/* Said plainly, because the two names doing different jobs is the
                thing most likely to confuse somebody here. */}
            <small>
              Tally calls it <strong>{picked.companyName || picked.name}</strong>. Renaming here changes
              only what this software shows — the connection still opens the company Tally knows.
            </small>
          </label>

          <label className="tb-field">
            <span>Attach to</span>
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Create a new client with the name above</option>
              {clients.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>
                  {c.displayName || c.name}
                </option>
              ))}
            </Select>
          </label>

          <div className="tb-confirm-actions">
            <Button variant="secondary" onClick={() => setPicked(null)} disabled={connecting}>Cancel</Button>
            <Button
              variant="primary"
              onClick={connect}
              disabled={connecting || (!clientId && !customName.trim())}
            >
              {connecting ? 'Connecting…' : clientId ? 'Connect and sync' : 'Create client and sync'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TallyBrowser;
